import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Date as DateComponent } from '../form/form';
import Axios from '../../../networking/intraceptor';
import * as endPoints from '../../../networking/urlEndPoints';
import {
  apiFreqToInternalRow,
  renderFrequencyRows,
} from '../../../utils/helper/investigationRx';
import {
  SAFE_SUPPLY_IV_CODE_COUNT_EVENT,
  SAFE_SUPPLY_IV_CODE_OPEN_EVENT,
} from './safeSupplyIvCodeReviewEvents';

const toPatientDetails = (record) => ({
  rxnumber: record?.rx_number,
  rxsafesupply: 'Y',
  is_fentanyl: 0,
  is_frequency: record?.is_frequency ? 1 : 0,
  intervention_code_values: Array.isArray(record?.intervention_code_values) && record.intervention_code_values.length > 0
    ? record.intervention_code_values
    : ['VS', 'SA', 'VSUF', 'UFSA'],
});

const SafeSupplyIvCodeReviewManager = ({ pharmacyId, isPharmacist = false }) => {
  const [records, setRecords] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editableRowsByRecord, setEditableRowsByRecord] = useState({});
  const [editableDatesByRecord, setEditableDatesByRecord] = useState({});
  const [saveErrorsByRecord, setSaveErrorsByRecord] = useState({});
  const [savingRecordId, setSavingRecordId] = useState(null);
  const previousOverflow = useRef(null);
  const [isExiting, setIsExiting] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const showDrawer = isOpen || isExiting;

  const fetchPendingReviews = useCallback(async () => {
    if (!pharmacyId || !isPharmacist) {
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await Axios.get(endPoints.pending_safe_supply_missing_iv_code_reviews);
      const nextRecords = data?.data?.records ?? [];

      setRecords(Array.isArray(nextRecords) ? nextRecords : []);

      if (Array.isArray(nextRecords) && nextRecords.length === 0) {
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Failed to load Safe Supply missing IV code reviews.', error);
    } finally {
      setIsLoading(false);
    }
  }, [isPharmacist, pharmacyId]);

  useEffect(() => {
    if (!pharmacyId || !isPharmacist) {
      setRecords([]);
      setIsOpen(false);
      return undefined;
    }

    fetchPendingReviews();

    const handleRealtimeEvent = (event) => {
      const detail = event?.detail;
      const payload = detail?.payload;

      if (detail?.event !== 'safe-supply-missing-iv-code-review') {
        return;
      }

      if (payload?.pharmacy_id && Number(payload.pharmacy_id) !== Number(pharmacyId)) {
        return;
      }

      fetchPendingReviews();
    };

    window.addEventListener('oatrx:realtime:event', handleRealtimeEvent);

    return () => {
      window.removeEventListener('oatrx:realtime:event', handleRealtimeEvent);
    };
  }, [fetchPendingReviews, isPharmacist, pharmacyId]);

  useEffect(() => {
    window.__oatrxSafeSupplyIvCodeCount = records.length;
    window.dispatchEvent(new CustomEvent(SAFE_SUPPLY_IV_CODE_COUNT_EVENT, {
      detail: { count: records.length },
    }));
  }, [records.length]);

  useEffect(() => {
    return () => {
      window.__oatrxSafeSupplyIvCodeCount = 0;
      window.dispatchEvent(new CustomEvent(SAFE_SUPPLY_IV_CODE_COUNT_EVENT, {
        detail: { count: 0 },
      }));
    };
  }, []);

  useEffect(() => {
    const handleOpenRequest = () => {
      if (isPharmacist && records.length > 0) {
        setIsOpen(true);
      }
    };

    window.addEventListener(SAFE_SUPPLY_IV_CODE_OPEN_EVENT, handleOpenRequest);

    return () => {
      window.removeEventListener(SAFE_SUPPLY_IV_CODE_OPEN_EVENT, handleOpenRequest);
    };
  }, [isPharmacist, records.length]);

  const handleClose = useCallback(() => {
    setHasOpened(false);
    setIsExiting(true);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose, isOpen]);

  const handleOpenSheet = (rxNumber) => {
    if (!rxNumber) {
      return;
    }

    window.location.href = `/pharmacy-investigate-rx?rx-number=${encodeURIComponent(rxNumber)}`;
  };

  const getDoseCount = (frequency) => ({
    OD: 1,
    BID: 2,
    TID: 3,
    QID: 4,
    '5x': 5,
    '6x': 6,
    '7x': 7,
  }[frequency] || 0);

  const sanitizeIvCode = (value) => {
    if (value == null || value === '') {
      return '';
    }

    return String(value).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
  };

  const normalizeFrequencyRows = (record) => {
    const sourceRows = Array.isArray(record?.frequency_rows) && record.frequency_rows.length > 0
      ? record.frequency_rows
      : [{ frequency: 'OD', dose_rows: [{ dose: '', iv_code: '' }], days: record?.day_supply || '' }];

    return sourceRows.map((row) => {
      const normalized = apiFreqToInternalRow(row);
      return {
        ...normalized,
        days: normalized.days || (record?.day_supply != null ? String(record.day_supply) : ''),
      };
    });
  };

  const getRecordKey = (record) => record?.pharmacy_winrx_rx_id || record?.id;

  const normalizeDateValue = (value) => {
    if (!value || value === '0000-00-00') {
      return '';
    }

    return String(value).slice(0, 10);
  };

  const shiftDate = (value, days) => {
    const normalizedValue = normalizeDateValue(value);
    if (!normalizedValue) {
      return '';
    }

    const [year, month, day] = normalizedValue.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12);
    date.setDate(date.getDate() + days);

    const nextYear = date.getFullYear();
    const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
    const nextDay = String(date.getDate()).padStart(2, '0');

    return `${nextYear}-${nextMonth}-${nextDay}`;
  };

  const normalizeEditableDates = (record) => ({
    ai_start_date: normalizeDateValue(record?.ai_start_date || record?.start_date || record?.rx_start_date),
    ai_stop_date: normalizeDateValue(record?.ai_stop_date || record?.ai_end_date || record?.stop_date) || shiftDate(record?.rx_stop_date, -1),
  });

  const setFrequencyRowsForRecord = (recordKey, record, updater) => {
    setEditableRowsByRecord((previous) => {
      const currentRows = previous[recordKey] || normalizeFrequencyRows(record);
      const nextRows = typeof updater === 'function' ? updater(currentRows) : updater;
      return { ...previous, [recordKey]: nextRows };
    });
    setSaveErrorsByRecord((previous) => ({ ...previous, [recordKey]: '' }));
  };

  const handleDateChange = (recordKey, field, value) => {
    setEditableDatesByRecord((previous) => ({
      ...previous,
      [recordKey]: {
        ...(previous[recordKey] || {}),
        [field]: value,
      },
    }));
    setSaveErrorsByRecord((previous) => ({ ...previous, [recordKey]: '' }));
  };

  useEffect(() => {
    if (!records.length) {
      setEditableRowsByRecord({});
      setEditableDatesByRecord({});
      return;
    }

    const nextRows = {};
    const nextDates = {};

    records.forEach((record) => {
      const recordKey = getRecordKey(record);
      if (!recordKey) {
        return;
      }

      nextRows[recordKey] = normalizeFrequencyRows(record);
      nextDates[recordKey] = normalizeEditableDates(record);
    });

    setEditableRowsByRecord(nextRows);
    setEditableDatesByRecord(nextDates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const validateRowsForSave = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return 'Frequency details are required.';
    }

    const invalidRow = rows.find((row) => {
      const expectedDoseCount = getDoseCount(row.frequency);
      return !row.frequency || expectedDoseCount === 0 || (row.dose_rows || []).length !== expectedDoseCount;
    });

    if (invalidRow) {
      return 'Dose rows must match the selected FQY.';
    }

    const missingDose = rows.some((row) => (row.dose_rows || []).some((doseRow) => String(doseRow.dose || '').trim() === ''));
    if (missingDose) {
      return 'Dose is required for each row.';
    }

    const missingIvCode = rows.some((row) => (row.dose_rows || []).some((doseRow) => String(doseRow.iv_code || '').trim() === ''));
    if (missingIvCode) {
      return 'IV code is required for each dose.';
    }

    return '';
  };

  const handleSaveFrequency = async (record) => {
    const recordKey = getRecordKey(record);
    const rows = editableRowsByRecord[recordKey] || normalizeFrequencyRows(record);
    const dates = editableDatesByRecord[recordKey] || normalizeEditableDates(record);
    const validationError = validateRowsForSave(rows);

    if (validationError) {
      setSaveErrorsByRecord((previous) => ({ ...previous, [recordKey]: validationError }));
      return;
    }

    if (!dates.ai_start_date || !dates.ai_stop_date) {
      setSaveErrorsByRecord((previous) => ({ ...previous, [recordKey]: 'Prep start and prep end dates are required.' }));
      return;
    }

    if (dates.ai_start_date > dates.ai_stop_date) {
      setSaveErrorsByRecord((previous) => ({ ...previous, [recordKey]: 'Prep end date must be after prep start date.' }));
      return;
    }

    const fallbackDays = record?.day_supply || 1;
    const payload = {
      rxes_id: recordKey,
      is_changing: false,
      is_non_dilaudid: record?.is_frequency ? true : false,
      frequency: rows.map((row) => ({
        frequency: row.frequency,
        dose_rows: (row.dose_rows || []).map((doseRow) => ({
          ...(doseRow.id ? { id: doseRow.id } : {}),
          dose: String(doseRow.dose ?? ''),
          iv_code: sanitizeIvCode(doseRow.iv_code),
        })),
        days: row.days || fallbackDays,
        ...(row.cycle_no ? { cycle_no: row.cycle_no } : {}),
      })),
    };

    setSavingRecordId(recordKey);
    setSaveErrorsByRecord((previous) => ({ ...previous, [recordKey]: '' }));

    try {
      await Axios.post(endPoints.save_ai_dates, {
        rx_number: record.rx_number,
        ai_start_date: dates.ai_start_date,
        ai_stop_date: dates.ai_stop_date,
      });
      await Axios.post('/update-prescription-billing-frequency', {
        rx_number: record.rx_number,
        billing_frequency: '1',
      });
      await Axios.post(endPoints.update_frequency_qty, payload);
      await fetchPendingReviews();
    } catch (error) {
      const message = error?.response?.data?.message || error?.response?.data?.error?.message || 'Unable to save IV code.';
      setSaveErrorsByRecord((previous) => ({ ...previous, [recordKey]: message }));
    } finally {
      setSavingRecordId(null);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previousOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setIsExiting(false);
    setHasOpened(false);

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setHasOpened(true));
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (showDrawer) {
      return undefined;
    }

    if (previousOverflow.current !== null) {
      document.body.style.overflow = previousOverflow.current;
      previousOverflow.current = null;
    }

    return undefined;
  }, [showDrawer]);

  useEffect(() => {
    return () => {
      if (previousOverflow.current !== null) {
        document.body.style.overflow = previousOverflow.current;
        previousOverflow.current = null;
      }
    };
  }, []);

  const handleTransitionEnd = (event) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }

    if (isExiting) {
      setIsExiting(false);
    }
  };

  if (!showDrawer) {
    return null;
  }

  const drawerTranslate = hasOpened && !isExiting ? 'translate-y-0' : 'translate-y-full';
  const backdropOpacity = hasOpened && !isExiting ? 'opacity-100' : 'opacity-0';

  return (
    <div className="fixed inset-0 z-[110] flex items-end">
      <div
        className={`absolute inset-0 bg-slate-900/35 backdrop-blur-[1px] transition-opacity duration-300 ease-out ${backdropOpacity}`}
        onClick={handleClose}
        aria-hidden
      />
      <section
        className={`relative max-h-[80vh] w-full rounded-t-2xl border-t border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out ${drawerTranslate}`}
        onTransitionEnd={handleTransitionEnd}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Safe Supply IV Code</h2>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close review panel"
        >
          <span className="text-xl leading-none">&times;</span>
        </button>
      </div>

      <div className="max-h-[calc(80vh-73px)] overflow-y-auto bg-slate-50">
        {isLoading && records.length === 0 ? (
          <div className="px-6 py-10 text-sm text-slate-500">Loading review records...</div>
        ) : null}

        {!isLoading && records.length === 0 ? (
          <div className="px-6 py-10 text-sm text-slate-500">
            No Safe Supply prescriptions missing Intervention Code today.
          </div>
        ) : null}

        {records.length > 0 ? (
          <div className="p-6">
            <div className="rounded-lg border border-slate-200 bg-white">
              <table className="w-full table-fixed divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="w-[90px] px-4 py-3 text-left font-semibold text-slate-700">RX</th>
                    <th className="w-[220px] px-4 py-3 text-left font-semibold text-slate-700">DRUG</th>
                    <th className="w-[320px] px-4 py-3 text-left font-semibold text-slate-700">SIG</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">SCHEDULE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {records.map((record) => {
                    const recordKey = getRecordKey(record);
                    const editableRows = editableRowsByRecord[recordKey] || normalizeFrequencyRows(record);
                    const editableDates = editableDatesByRecord[recordKey] || normalizeEditableDates(record);
                    const scheduleFormData = {
                      start_date: editableDates.ai_start_date,
                      stop_date: editableDates.ai_stop_date,
                    };
                    const patientDetails = toPatientDetails(record);
                    const isSaving = savingRecordId === recordKey;

                    return (
                      <tr key={recordKey} className="align-middle hover:bg-slate-50">
                        <td className="whitespace-nowrap px-4 py-3 font-medium">
                          {record.rx_number ? (
                            <button
                              type="button"
                              onClick={() => handleOpenSheet(record.rx_number)}
                              className="text-left font-semibold text-slate-900 underline-offset-2 hover:text-blue-700 hover:underline"
                            >
                              {record.rx_number}
                            </button>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700 break-words">
                          {record.drug || '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-700 break-words">
                          {record.rxsig || '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className={`flex flex-wrap items-end gap-2 text-sm uppercase ${!scheduleFormData.start_date || !scheduleFormData.stop_date ? 'pb-4' : ''}`}>
                            <div className="flex shrink-0 flex-nowrap items-end gap-2">
                              <DateComponent
                                formData={scheduleFormData}
                                setFormData={(updater) => {
                                  const next = typeof updater === 'function'
                                    ? updater(scheduleFormData)
                                    : updater;
                                  if (next?.start_date != null) {
                                    handleDateChange(recordKey, 'ai_start_date', next.start_date);
                                  }
                                }}
                                handleFormDataChange={(event) => handleDateChange(recordKey, 'ai_start_date', event.target.value)}
                                label="PREP START DATE"
                                name="start_date"
                                dValue={scheduleFormData.start_date}
                                type="removeToday"
                                className="rounded shrink-0 w-[250px]"
                                height="38px"
                                labelClassName="ml-2 bg-white rounded"
                                error={!scheduleFormData.start_date}
                                id={`start_date_${recordKey}`}
                                validateYear
                                univalue={`safe_supply_start_date_${recordKey}`}
                              />

                              <DateComponent
                                formData={scheduleFormData}
                                setFormData={(updater) => {
                                  const next = typeof updater === 'function'
                                    ? updater(scheduleFormData)
                                    : updater;
                                  if (next?.stop_date != null) {
                                    handleDateChange(recordKey, 'ai_stop_date', next.stop_date);
                                  }
                                }}
                                handleFormDataChange={(event) => handleDateChange(recordKey, 'ai_stop_date', event.target.value)}
                                label="PREP END DATE"
                                name="stop_date"
                                dValue={scheduleFormData.stop_date}
                                type="removeToday"
                                className="rounded shrink-0 w-[250px] bg-gray-50"
                                height="38px"
                                labelClassName="ml-2 rounded bg-gray-50"
                                error={!scheduleFormData.stop_date}
                                id={`stop_date_${recordKey}`}
                              />
                            </div>

                            <div className="contents [&>div]:!contents [&>div>div]:!contents">
                              {renderFrequencyRows({
                                isDilaudid: true,
                                frequencyRows: editableRows,
                                setFrequencyRows: (updater) => setFrequencyRowsForRecord(recordKey, record, updater),
                                patientDetails,
                                is_dilaudid: record?.is_dilaudid ?? true,
                                pacmed_overwrite: record?.pacmed_overwrite ?? false,
                                doseErrors: [],
                                removeDays: true,
                                hideIvCodeUpdatedBy: true,
                              })}
                            </div>

                            <button
                              type="button"
                              onClick={() => handleSaveFrequency(record)}
                              disabled={isSaving}
                              className={`shrink-0 text-white px-4 py-2 h-[42px] rounded-md w-fit uppercase ${isSaving ? 'bg-[#1A56DB]/50 cursor-not-allowed' : 'bg-[#1A56DB] hover:bg-[#1851c8]'}`}
                            >
                              {isSaving ? 'Updating...' : 'Update'}
                            </button>
                          </div>

                          {saveErrorsByRecord[recordKey] ? (
                            <p className="mt-1 text-sm font-medium text-red-600 normal-case">{saveErrorsByRecord[recordKey]}</p>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
      </section>
    </div>
  );
};

export default SafeSupplyIvCodeReviewManager;
